import { useEffect, useState } from 'react'

import {
  TailgateGridContext,
  useTailgateGrid,
} from '../../hooks/useTailgateGrid'
import { useUpdateTailgateGrid } from '../../hooks/useUpdateTailgateGrid'
import {
  TIMEOUT_MESSAGE,
  useGridAnalysisJob,
} from '../hooks/useGridAnalysisJob'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { HiOutlineBeaker } from 'react-icons/hi'

import {
  Flex,
  Icon,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
} from '@chakra-ui/react'

import {
  Box as DarkBox,
  Button as DarkButton,
  Modal as DarkModal,
  tokenPrefix,
  tokenVar,
} from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { Modal as LegacyModal } from '@/components/overlays'
import { GridAnalysisJobStatus } from '@/graphql/generated/schemas'
import {
  GTZ_JOB_COMPLETED,
  GTZ_JOB_FAILED,
  GTZ_RUN_TEST,
  GTZ_SAVE_GRID,
} from '@/utils/analytics'

import {
  BoxType,
  CornerPoint,
  IncidentOptionI,
  TailgateBox,
  TailgateGridClick,
} from '../../types/types'
import {
  EMPTY_BOX,
  N_CORNERS,
  getBoxInput,
  isValidBox,
} from '../../utils/utils'
import { ExitGridTestingConfirmDialog } from './ExitGridTestingConfirmDialog'
import { GridEditingStep } from './GridEditingStep'
import { IncidentSelectExpectedCountsStep } from './IncidentSelectExpectedCountsStep'
import { InstructionsStep } from './InstructionsStep'
import { ProcessingStep } from './ProcessingStep'
import { ResultsStep } from './ResultsStep'
import { SaveGridConfirmDialog } from './SaveGridConfirmDialog'

interface GridTestingZoneIProps {
  colorScheme?: ColorScheme
  isOpen: boolean
  onClose: () => void
  /**
   * When provided (e.g. launched from the Incident Drawer), the count-entry
   * step defaults to a "preset" mode that displays the pre-populated incident
   * on the left panel. The user can toggle to the search table and back.
   */
  presetIncident?: IncidentOptionI | null
  zIndex?: number
}

export const GridTestingZone = ({
  colorScheme,
  isOpen,
  onClose,
  presetIncident,
  zIndex,
}: GridTestingZoneIProps) => {
  const outerContext = useTailgateGrid()
  const { isImplementer } = usePermissions()
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1)
  const [testInBox, setTestInBox] = useState<TailgateBox | null>(null)
  const [testOutBox, setTestOutBox] = useState<TailgateBox | null>(null)
  const [peopleIn, setPeopleIn] = useState(0)
  const [peopleOut, setPeopleOut] = useState(0)
  const [selectedIncident, setSelectedIncident] =
    useState<IncidentOptionI | null>(null)
  const [isExitConfirmOpen, setShowExitConfirm] = useState(false)
  const [isSaveConfirmOpen, setShowSaveConfirm] = useState(false)

  const { showError } = useToasts()
  const { start, reset, job, status, isTimeout } = useGridAnalysisJob({
    onCompleted: () => outerContext.trackTailgateEvent?.(GTZ_JOB_COMPLETED),
    onFailed: (timedOut) => {
      outerContext.trackTailgateEvent?.(GTZ_JOB_FAILED)
      showError(
        timedOut
          ? TIMEOUT_MESSAGE
          : 'Grid analysis job failed. Please try again.'
      )
    },
  })

  const { isUpdateLoading, updateGrid } = useUpdateTailgateGrid({
    associatedDevices: outerContext.associateDeviceValues,
    // Close BOTH the testing zone AND the outer TailgateGridModal on save.
    // Saving in the results step means "done" — the user expects to return
    // to the main UI, not to the tailgate grid modal.
    onClose: () => {
      resetAndClose()
      outerContext.onTailgateGridClose?.()
    },
  })

  // Snapshot state when modal opens
  useEffect(() => {
    if (isOpen) {
      setTestInBox({
        ...outerContext.inBox,
        corners: outerContext.inBox?.corners?.map((c) => ({ ...c })) ?? [],
      })
      setTestOutBox({
        ...outerContext.outBox,
        corners: outerContext.outBox?.corners?.map((c) => ({ ...c })) ?? [],
      })
    }
  }, [isOpen, outerContext.inBox, outerContext.outBox])

  const resetAndClose = () => {
    reset()
    setStep(1)
    setTestInBox(null)
    setTestOutBox(null)
    setPeopleIn(0)
    setPeopleOut(0)
    setSelectedIncident(null)
    setShowExitConfirm(false)
    onClose()
  }

  const handleClose = () => {
    if (step === 1) {
      resetAndClose()
    } else {
      setShowExitConfirm(true)
    }
  }

  const isTestGridValid =
    testInBox?.isValid &&
    testOutBox?.isValid &&
    testInBox?.corners?.length === N_CORNERS &&
    testOutBox?.corners?.length === N_CORNERS

  const handleRunTest = async () => {
    if (!selectedIncident || !isTestGridValid) return
    setStep(4)
    outerContext.trackTailgateEvent?.(GTZ_RUN_TEST)
    await start({
      incidentId: selectedIncident.id,
      labeledIn: peopleIn,
      labeledOut: peopleOut,
      inBox: getBoxInput(testInBox),
      outBox: getBoxInput(testOutBox),
    })
  }

  const handleSelectAnotherIncident = () => {
    reset()
    setStep(3)
    setSelectedIncident(null)
    setPeopleIn(0)
    setPeopleOut(0)
  }

  const handleEnterTestingZone = () => setStep(2)

  const handleOpenIncidentSelection = () => setStep(3)

  const handleBackToGridEditing = () => {
    setSelectedIncident(null)
    setPeopleIn(0)
    setPeopleOut(0)
    setStep(2)
  }

  const handleRetry = () => {
    void handleRunTest()
  }

  const handleSaveGrid = () => {
    if (!testInBox || !testOutBox) return
    setShowSaveConfirm(true)
  }

  const handleConfirmSave = () => {
    if (!testInBox || !testOutBox) return
    outerContext.trackTailgateEvent?.(GTZ_SAVE_GRID)
    setShowSaveConfirm(false)
    updateGrid(testInBox, testOutBox, outerContext.videoDimensions)
  }

  const addTestBoxCorner = (
    point: { x: number; y: number },
    boxType: BoxType,
    setBox: (value: TailgateBox | ((prev: TailgateBox) => TailgateBox)) => void
  ) => {
    if (!outerContext.scaleX || !outerContext.scaleY) return
    setBox((b) => {
      const newCorner: CornerPoint = {
        x: point.x,
        y: point.y,
        scaledX: outerContext.scaleX(point.x),
        scaledY: outerContext.scaleY(point.y),
        id: b.corners.length + 1,
        type: boxType,
      }
      const updated = { ...b, corners: [...b.corners, newCorner] }
      if (updated.corners.length === N_CORNERS) {
        return { ...updated, isValid: isValidBox(updated) }
      }
      return updated
    })
  }

  const handleTestClick = (e: TailgateGridClick) => {
    if (!testInBox || !testOutBox) return
    const rect = e.currentTarget.getBoundingClientRect()
    const position = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    }
    if (testInBox.corners.length < N_CORNERS) {
      addTestBoxCorner(position, BoxType.IN_BOX, setTestInBox)
    } else if (testOutBox.corners.length < N_CORNERS) {
      addTestBoxCorner(position, BoxType.OUT_BOX, setTestOutBox)
    }
  }

  // Build an isolated context that prevents mutations from leaking to the parent
  const testContextValue =
    testInBox && testOutBox
      ? {
          ...outerContext,
          inBox: testInBox,
          outBox: testOutBox,
          setInBox: setTestInBox,
          setOutBox: setTestOutBox,
          clearBoxes: () => {
            setTestInBox(EMPTY_BOX)
            setTestOutBox(EMPTY_BOX)
          },
          setInitialBoxes: () => {
            setTestInBox({
              ...outerContext.inBox,
              corners:
                outerContext.inBox?.corners?.map((c) => ({ ...c })) ?? [],
            })
            setTestOutBox({
              ...outerContext.outBox,
              corners:
                outerContext.outBox?.corners?.map((c) => ({ ...c })) ?? [],
            })
          },
          handleOnClick: handleTestClick,
          refreshSnapshot: outerContext.refreshSnapshot,
          resetVideoFrame: outerContext.resetVideoFrame,
        }
      : null

  if (!isOpen || !testContextValue || !isImplementer) return null

  const modalSize = step === 1 ? '2xl' : step === 4 ? '3xl' : '6xl'
  const isProcessing = step === 4 && status === GridAnalysisJobStatus.Pending

  const displayStep =
    step === 2 ? 1 : step === 3 ? 2 : isProcessing ? 3 : step === 4 ? 4 : null

  const currentStepContent = (
    <>
      {step === 1 && (
        <InstructionsStep
          colorScheme={colorScheme}
          onBegin={handleEnterTestingZone}
          onClose={resetAndClose}
        />
      )}

      {step === 2 && (
        <GridEditingStep
          colorScheme={colorScheme}
          isNextDisabled={!isTestGridValid}
          onClose={handleClose}
          onNext={handleOpenIncidentSelection}
        />
      )}

      {step === 3 && (
        <IncidentSelectExpectedCountsStep
          colorScheme={colorScheme}
          isRunTestDisabled={!isTestGridValid}
          onBack={handleBackToGridEditing}
          onClose={handleClose}
          onRunTest={handleRunTest}
          peopleIn={peopleIn}
          peopleOut={peopleOut}
          presetIncident={presetIncident}
          selectedIncident={selectedIncident}
          setPeopleIn={setPeopleIn}
          setPeopleOut={setPeopleOut}
          setSelectedIncident={setSelectedIncident}
        />
      )}

      {step === 4 && status === GridAnalysisJobStatus.Pending && (
        <ProcessingStep colorScheme={colorScheme} />
      )}

      {step === 4 && status === GridAnalysisJobStatus.Completed && job && (
        <ResultsStep
          colorScheme={colorScheme}
          isSaving={isUpdateLoading}
          job={job}
          labeledIn={peopleIn}
          labeledOut={peopleOut}
          onClose={handleClose}
          onSaveGrid={handleSaveGrid}
          onSelectAnotherIncident={handleSelectAnotherIncident}
          selectedIncident={selectedIncident}
          variant='success'
        />
      )}

      {step === 4 && status === GridAnalysisJobStatus.Failed && (
        <ResultsStep
          colorScheme={colorScheme}
          isTimeout={isTimeout}
          onClose={handleClose}
          onRetry={handleRetry}
          onSelectAnotherIncident={handleSelectAnotherIncident}
          selectedIncident={selectedIncident}
          variant='error'
        />
      )}
    </>
  )

  if (colorScheme) {
    const tokenPathPrefix = tokenPrefix(colorScheme)
    const secondaryTextColor = tokenVar(
      `colors.${tokenPathPrefix}text.secondary`
    )
    const bannerBackground = tokenVar(`colors.${tokenPathPrefix}status.info-bg`)
    const bannerBorder = tokenVar(`colors.${tokenPathPrefix}border.default`)
    const bannerText = tokenVar(`colors.${tokenPathPrefix}status.info-text`)

    const modalTitle =
      step === 1
        ? 'Grid Testing Zone'
        : step === 2
        ? 'Grid Configuration'
        : step === 3
        ? 'Select Incident & Set Expected Counts'
        : status === GridAnalysisJobStatus.Completed
        ? 'Grid Testing Results'
        : status === GridAnalysisJobStatus.Failed
        ? isTimeout
          ? 'Test Timed Out'
          : 'Test Failed'
        : 'Calculating Grid Accuracy Results'

    const modalSubtitle =
      step === 2
        ? "Adjust the camera grids below in order to test the configuration's performance"
        : step === 3
        ? 'Select an incident and set expected IN/OUT counts to test grid accuracy'
        : step === 4 && status === GridAnalysisJobStatus.Completed
        ? `Review the test grid's results for ${selectedIncident?.incidentId}`
        : step === 4 && status === GridAnalysisJobStatus.Failed
        ? selectedIncident
          ? `Results for ${selectedIncident.incidentId}`
          : 'Grid Testing Zone'
        : undefined

    const footer = (
      <DarkBox display='flex' gap='8px'>
        {step === 1 && (
          <>
            <DarkButton
              colorScheme={colorScheme}
              onClick={resetAndClose}
              variant='tertiary'
            >
              Cancel
            </DarkButton>
            <DarkButton
              colorScheme={colorScheme}
              onClick={handleEnterTestingZone}
              variant='primary'
            >
              Enter
            </DarkButton>
          </>
        )}
        {step === 2 && (
          <>
            <DarkButton
              colorScheme={colorScheme}
              onClick={handleClose}
              variant='tertiary'
            >
              Cancel
            </DarkButton>
            <DarkButton
              colorScheme={colorScheme}
              disabled={!isTestGridValid}
              onClick={handleOpenIncidentSelection}
              variant='primary'
            >
              Test Grid
            </DarkButton>
          </>
        )}
        {step === 3 && (
          <>
            <DarkButton
              colorScheme={colorScheme}
              onClick={handleBackToGridEditing}
              variant='secondary'
            >
              Back
            </DarkButton>
            <DarkButton
              colorScheme={colorScheme}
              onClick={handleClose}
              variant='tertiary'
            >
              Cancel
            </DarkButton>
            <DarkButton
              colorScheme={colorScheme}
              disabled={!selectedIncident || !isTestGridValid}
              onClick={handleRunTest}
              variant='primary'
            >
              Run Test
            </DarkButton>
          </>
        )}
        {step === 4 && status === GridAnalysisJobStatus.Completed && (
          <>
            <DarkButton
              colorScheme={colorScheme}
              onClick={handleClose}
              variant='tertiary'
            >
              Cancel
            </DarkButton>
            <DarkButton
              colorScheme={colorScheme}
              onClick={handleSelectAnotherIncident}
              variant='secondary'
            >
              Select Another Incident
            </DarkButton>
            <DarkButton
              colorScheme={colorScheme}
              disabled={isUpdateLoading}
              isLoading={isUpdateLoading}
              onClick={handleSaveGrid}
              variant='primary'
            >
              Save New Camera Grid
            </DarkButton>
          </>
        )}
        {step === 4 && status === GridAnalysisJobStatus.Failed && (
          <>
            <DarkButton
              colorScheme={colorScheme}
              onClick={handleClose}
              variant='tertiary'
            >
              Cancel
            </DarkButton>
            <DarkButton
              colorScheme={colorScheme}
              onClick={handleSelectAnotherIncident}
              variant='secondary'
            >
              Select Another Incident
            </DarkButton>
            <DarkButton
              colorScheme={colorScheme}
              onClick={handleRetry}
              variant='primary'
            >
              Retry
            </DarkButton>
          </>
        )}
      </DarkBox>
    )

    return (
      <TailgateGridContext.Provider value={testContextValue}>
        <DarkModal
          bodyStyleOverrides={{ overflowY: 'auto', pt: '24px' }}
          colorScheme={colorScheme}
          footer={isProcessing ? undefined : footer}
          header={
            modalSubtitle ? (
              <DarkBox color={secondaryTextColor}>{modalSubtitle}</DarkBox>
            ) : undefined
          }
          isCloseHidden={isProcessing}
          isOpen={isOpen}
          isSaveHidden
          maxW={step === 1 ? '672px' : step === 4 ? '768px' : undefined}
          onClose={handleClose}
          size={step === 1 ? 'compact' : 'default'}
          title={modalTitle}
          zIndex={zIndex}
        >
          {step > 1 && (
            <DarkBox
              alignItems='center'
              bg={bannerBackground}
              border='1px solid'
              borderColor={bannerBorder}
              color={bannerText}
              display='flex'
              justifyContent='space-between'
              mb='20px'
              px='12px'
              py='8px'
            >
              <DarkBox alignItems='center' display='flex' gap='8px'>
                <HiOutlineBeaker size='18px' />
                <DarkBox textStyle='caption-medium'>Grid Testing Zone</DarkBox>
              </DarkBox>
              <DarkBox textStyle='caption'>Step {displayStep} of 4</DarkBox>
            </DarkBox>
          )}

          {currentStepContent}
        </DarkModal>
        <ExitGridTestingConfirmDialog
          colorScheme={colorScheme}
          isOpen={isExitConfirmOpen}
          onCancel={() => setShowExitConfirm(false)}
          onConfirm={resetAndClose}
          zIndex={zIndex === undefined ? undefined : zIndex + 100}
        />
        <SaveGridConfirmDialog
          colorScheme={colorScheme}
          isOpen={isSaveConfirmOpen}
          isSaving={isUpdateLoading}
          onCancel={() => setShowSaveConfirm(false)}
          onConfirm={handleConfirmSave}
          zIndex={zIndex === undefined ? undefined : zIndex + 100}
        />
      </TailgateGridContext.Provider>
    )
  }

  return (
    <TailgateGridContext.Provider value={testContextValue}>
      <LegacyModal
        isCentered
        isOpen={isOpen}
        motionPreset='slideInBottom'
        onClose={handleClose}
        scrollBehavior={step === 2 ? 'inside' : 'outside'}
        size={modalSize}
      >
        <ModalOverlay />
        <ModalContent
          bg='#fff'
          border='2px solid'
          borderColor='purple.400'
          px={{ base: '5px', sm: '15px', md: '30px' }}
          py='30px'
        >
          {!isProcessing && (
            <ModalCloseButton color='#000' mt='5px' opacity={0.4} />
          )}

          {step > 1 && (
            <Flex
              align='center'
              bg='purple.50'
              border='1px solid'
              borderColor='purple.200'
              borderRadius='md'
              gap={2}
              justifyContent='space-between'
              mb={5}
              mr={5}
              mt={1}
              px={3}
              py={2}
            >
              <Flex align='center' gap={2}>
                <Icon as={HiOutlineBeaker} boxSize='18px' color='purple.600' />
                <Text color='purple.700' fontSize='sm' fontWeight='semibold'>
                  Grid Testing Zone
                </Text>
              </Flex>
              <Text color='purple.500' fontSize='xs' fontWeight='medium'>
                Step {displayStep} of 4
              </Text>
            </Flex>
          )}

          {currentStepContent}
        </ModalContent>
      </LegacyModal>
      <ExitGridTestingConfirmDialog
        isOpen={isExitConfirmOpen}
        onCancel={() => setShowExitConfirm(false)}
        onConfirm={resetAndClose}
      />
      <SaveGridConfirmDialog
        isOpen={isSaveConfirmOpen}
        isSaving={isUpdateLoading}
        onCancel={() => setShowSaveConfirm(false)}
        onConfirm={handleConfirmSave}
      />
    </TailgateGridContext.Provider>
  )
}
